import { generateId } from './storageService';

// Enum para representar TipoUser da API
export enum TipoUsuario {
  Vendedor = 1,
  Gestor = 2,
  Cozinheiro = 3,
  Entregador = 4,
  Operador = 5
}

export interface User {
  id: string;
  name: string;
  email: string;
  role: 'admin' | 'waiter' | 'kitchen' | 'delivery'; // Updated role types
  password?: string;
  avatar?: string;
  isManager?: boolean; // Added isManager property
  username?: string;   // Added username property
  tipoUsuario?: TipoUsuario; // Novo campo para armazenar o TipoUser da API
  vendedor_id?: string | number; // Added for ERP integration
  empresa_id?: string | number;  // Added for ERP integration
}

// Initialize auth storage with default admin user
export const initializeAuthStorage = () => {
  const users = getUsers();
  
  if (users.length === 0) {
    // Create default admin user
    const defaultAdmin: User = {
      id: generateId(),
      name: 'Administrador',
      email: 'admin@example.com',
      role: 'admin',
      password: 'admin123'
    };
    
    addUser(defaultAdmin);
  }
  
  // Check if there's a persisted login
  const persistedLogin = localStorage.getItem('currentUser');
  if (persistedLogin) {
    // User is already logged in from a previous session
    const user = JSON.parse(persistedLogin);
    localStorage.setItem('isAuthenticated', 'true');
  } else {
    localStorage.removeItem('isAuthenticated');
  }
};

// Get all users
export const getUsers = (): User[] => {
  const stored = localStorage.getItem('restaurant_users');
  return stored ? JSON.parse(stored) : [];
};

// Add a new user
export const addUser = (user: User): void => {
  const users = getUsers();
  
  // Check if email already exists
  if (users.some(u => u.email === user.email)) {
    throw new Error('Email already in use');
  }
  
  users.push(user);
  localStorage.setItem('restaurant_users', JSON.stringify(users));
};

// Update user
export const updateUser = (user: User): void => {
  const users = getUsers();
  const index = users.findIndex(u => u.id === user.id);
  
  if (index !== -1) {
    users[index] = { ...users[index], ...user };
    localStorage.setItem('restaurant_users', JSON.stringify(users));
  }
};

// Remove user
export const removeUser = (id: string): void => {
  const users = getUsers().filter(u => u.id !== id);
  localStorage.setItem('restaurant_users', JSON.stringify(users));
};

// Login
export const login = (email: string, password: string): Omit<User, 'password'> | null => {
  const users = getUsers();
  const user = users.find(u => u.email === email && u.password === password);
  
  if (user) {
    const { password, ...userWithoutPassword } = user;
    localStorage.setItem('currentUser', JSON.stringify(userWithoutPassword));
    localStorage.setItem('isAuthenticated', 'true');
    return userWithoutPassword;
  }
  
  return null;
};

// Logout
export const logout = (): void => {
  localStorage.removeItem('currentUser');
  localStorage.removeItem('isAuthenticated');
};

// Check if user is authenticated
export const isAuthenticated = (): boolean => {
  return localStorage.getItem('isAuthenticated') === 'true';
};

// Get current user
export const getCurrentUser = (): Omit<User, 'password'> | null => {
  const stored = localStorage.getItem('currentUser');
  return stored ? JSON.parse(stored) : null;
};

// Get API token
export const getApiToken = (): string | null => {
  const token = localStorage.getItem('api_auth_token');
  if (!token) return null;
  
  // Try to decode base64 if it's encoded
  try {
    return atob(token);
  } catch (e) {
    return token; // If decoding fails, return as is
  }
};

// Mapear TipoUsuario string ou number para role do sistema
export const mapTipoUsuarioToRole = (tipoUsuario: TipoUsuario | string | number): User['role'] => {
  // Se tipoUsuario for string, converter para o enum correspondente
  if (typeof tipoUsuario === 'string') {
    switch (tipoUsuario) {
      case 'Gestor':
        return 'admin';
      case 'Vendedor':
        return 'waiter';
      case 'Cozinheiro':
        return 'kitchen';
      case 'Entregador':
        return 'delivery';
      case 'Operador':
        return 'waiter'; // Operador como garçom por padrão
      default:
        return 'waiter'; // Padrão para tipos desconhecidos
    }
  }
  
  // Se tipoUsuario for número ou enum
  if (typeof tipoUsuario === 'number') {
    switch (tipoUsuario) {
      case TipoUsuario.Gestor:
        return 'admin';
      case TipoUsuario.Vendedor:
        return 'waiter';
      case TipoUsuario.Cozinheiro:
        return 'kitchen';
      case TipoUsuario.Entregador:
        return 'delivery';
      case TipoUsuario.Operador:
        return 'waiter'; // Operador como garçom por padrão
      default:
        return 'waiter'; // Padrão para tipos desconhecidos
    }
  }
  
  // Caso padrão (não deveria chegar aqui)
  return 'waiter';
};

// Converter string ou número para TipoUsuario
export const stringOrNumberToTipoUsuario = (value: string | number): TipoUsuario => {
  if (typeof value === 'string') {
    switch (value) {
      case 'Gestor':
        return TipoUsuario.Gestor;
      case 'Vendedor':
        return TipoUsuario.Vendedor;
      case 'Cozinheiro':
        return TipoUsuario.Cozinheiro;
      case 'Entregador':
        return TipoUsuario.Entregador;
      case 'Operador':
        return TipoUsuario.Operador;
      default:
        return TipoUsuario.Vendedor; // Padrão para strings desconhecidas
    }
  }

  // Se for número, tentar converter diretamente
  return value as TipoUsuario;
};

// Verificar se o tipo de usuário é gestor
export const isTipoUsuarioGestor = (tipoUsuario: TipoUsuario | string | number): boolean => {
  if (typeof tipoUsuario === 'string') {
    return tipoUsuario === 'Gestor';
  }
  
  if (typeof tipoUsuario === 'number') {
    return tipoUsuario === TipoUsuario.Gestor;
  }
  
  return false;
};
